/**
 * See VideoDesk confluence page as well
 * https://arrowecommerce.atlassian.net/wiki/display/ARW/Video+Desk
 */
/*jshint camelcase: false */
'use strict';

import $ from 'jquery';
import logger from 'loglevel';
import _ from 'underscore';
import mediator from '../../js/mediator';
import AgentsCollection from './collections/agentsCollection';
import VideoDeskBlock from './views/videoDeskBlock';
import AgentListView from './views/agentListView';
import VideoDeskBaseController from './VideoDeskBaseController';

import {AgentChatButton} from '../../utilities/featureFlags';

const log = logger.getLogger('VideoDesk');

export default class VideoDeskUIController extends VideoDeskBaseController {
  constructor(videoChatEngineers) {
    super({
      abstraction: 'on' // Enable the abstraction UI
    });

    $('body').addClass('LiveSupportEnabled');

    this.setGlobalOptions(videoChatEngineers);
    this.createAgentsCollections();
    this.buildAgentList();
    this.createPreferentialBlockView();
    this.createSpecificAgentBlockViews();
    this.bindVsCallback();
  }

  /**
   * The agents state collection is a full list of all the
   * known engineers given to us via the global object
   * rendered into the page on the server
   *
   * It is sorted by the index field
   *
   * The Agents list collection holds the same models but is sorted
   * by agent availability
   */
  createAgentsCollections() {
    this.agentsStateCollection = new AgentsCollection(null, { comparator: 'index' });
    this.agentListCollection = new AgentsCollection();
    this.setAgentsState(this.globals.engineers, true);
  }

  /**
   * Globals holds translations for video desk used in
   * the templates
   *
   * It also holds some global options for how to display
   * the agent blocks
   *
   * "enableprofilelinks": true,
   * "enablemessagecta": true,
   * "enablerealtimeupdates": true,
   * "enablebusyoverlay": true,
   * "profileheading": "Need Help?",
   * "profilesubheading": "",
   * "profilectatext": "VIDEO CHAT NOW",
   * "contactusheading": "179 Engineers are here to assist",
   * "contactussubheading": "An online engineer will appear here when available to VideoChat, otherwise search our engineers to email your question. ",
   * "contactuslink": "https://www.arrow.com/en/design-center/engineers",
   * "contactuslinktext": "Search Engineers",
   * "showcontactus": false => A global toggle to only show contact/email us buttons rather than VideoDesk
   *
   */
  setGlobalOptions(videoChatEngineers) {
    this.globals = videoChatEngineers;
  }

  /**
   * This takes a raw array of agents json and keeps
   * the agents collections in sync
   *
   * The agents array could originate from two different
   * places which hold different properties.
   *
   * However they both have the same `uid` property which
   * refers to the same agent in both and is specified as
   * the idAttribute in the AgentModel
   *
   * From Sitecore
   *
   *   [
   *     {
   *       "uid":"ec12491465b2fc3bb444bf933aef4241",
   *       "index":1,
   *       "engineername":"Test Engineer",
   *       "engineeremail":"  ",
   *       "engineerimage":"https://static4qa.arrow.com/~/media/images/engineer-chat/adam-kattnig-engineer-chat.jpg?mw=399&hash=D3DBEEF394735AAA1783F9B67DE568B3E3BC0263",
   *       "tags":["Memory","Displays","Embedded HW/SW"],
   *       "engineerProfilePageUrl":"/en/design-center/engineers/test-engineer",
   *       "authorized":true,
   *       "job":"Engineer",
   *       "isVideoEngineer":true
   *     }
   *   ]
   *
   * From VideoDesk
   *
   *   [
   *     {
   *       "uid": "1eaa3547936eeed72dd8459b9b730a2f",
   *       "avatar": "https://bo1.videodesk.com/uploads/agent-avatars/no_operator_small.jpg",
   *       "username": "R\u00e9gis Duval",
   *       "job": "Agent",
   *       "available_mode": "unavailable",
   *       "is_text_capable": false,
   *       "is_audio_capable": false,
   *       "is_video_capable": false
   *     }
   *   ]
   *
   * @param agents
   */
  setAgentsState(agents, addNew = false) {
    if (!_.isArray(agents)) return;
    let changedAgents = this.agentsStateCollection.set(agents, {
      add: addNew,
      remove: false,
      sort: false
    });
    log.debug('VideoDesk:changed agents', changedAgents);
    log.debug('VideoDesk:total agents', this.agentsStateCollection.length);
    if (this.agentListCollection) {
      this.agentListCollection.reset(this.agentsStateCollection.models, {
        sort: false
      });
    }
    this.agentsStateCollection.sort();
    this.agentListCollection.sort();
    let agentsOnlyInVideoDesk = agents.filter((agent) => {
      return !this.agentsStateCollection.get(agent.uid);
    });
    if (agentsOnlyInVideoDesk.length) {
      log.info('VideoDesk:agents only in VideoDesk and not in SiteCore based on UID (These will not be added to list)', agentsOnlyInVideoDesk);
    }
  }

  assignPreferredModel() {
    // jscs:disable requireCamelCaseOrUpperCaseIdentifiers
    this.preferredAvailableModel = this.agentsStateCollection.findWhere({
      is_text_capable: true,
      authorized: true
    });
    // jscs:enable requireCamelCaseOrUpperCaseIdentifiers
    if (this.preferredAvailableModel) {
      mediator.trigger('videodesk:preferredAvailableModel', this.preferredAvailableModel);
    } else {
      log.warn('no authorized agents available');
      mediator.trigger('videodesk:noAgentsAvailable');
    }

  }

  /**
   * This attaches the documented VideoDesk callback to
   * the window.
   *
   * Docs: http://doc.videodesk.com/availability_monitoring.html
   */
  bindVsCallback() {
    this.getAvailabilityTimeout = setTimeout(() => {
      mediator.trigger('videodesk:noAgentsAvailable');
    }, 30000);
    window._videodeskGetAvailability = this.setAvailability.bind(this);
  }

  /**
   * Set internal state of the agents based on the availability response
   * from VideoDesk
   * @param videoDeskAvailability Here's a sample response
   *
   *    {
   *      "agents": {
   *        "1eaa3547936eeed72dd8459b9b730a2f": {
   *          "uid": "1eaa3547936eeed72dd8459b9b730a2f",
   *          "avatar": "https://bo1.videodesk.com/uploads/agent-avatars/no_operator_small.jpg",
   *          "username": "R\u00e9gis Duval",
   *          "job": "Agent",
   *          "available_mode": "unavailable",
   *          "is_text_capable": false,
   *          "is_audio_capable": false,
   *          "is_video_capable": false
   *        },
   *      },
   *      "call_mode": "agentlist",
   *      "nb_agents_available": 2,
   *      "available_video": true,
   *      "available_audio": true,
   *      "available_chat": true,
   *      "is_text_capable": true,
   *      "is_audio_capable": true,
   *      "is_video_capable": false
   *    }
   *
   */
  setAvailability(videoDeskAvailability) {
    clearTimeout(this.getAvailabilityTimeout);

    if (AgentChatButton.isEnabled()) {
      this.globals.isUserAbleToChat = this.checkUserCanChat(videoDeskAvailability);
      this.globals.isUserVideoOk = window._vdk.isWebuserSupportVideo('videowithoutaudio').capable;
    } else {
      this.globals.isUserVideoOk = this.checkUserCanChat(videoDeskAvailability);
      this.globals.isUserAbleToChat = false;
    }
    log.debug('VideoDesk:userCanChat', this.globals.isUserAbleToChat);
    log.debug('VideoDesk:availability', videoDeskAvailability);
    log.debug('VideoDesk:agentsAvailable', videoDeskAvailability.nb_agents_available);

    let agents = _.toArray(videoDeskAvailability.agents);
    this.setAgentsState(agents);

    if (videoDeskAvailability.nb_agents_available > 0) {
      this.assignPreferredModel();
    } else {
      mediator.trigger('videodesk:noAgentsAvailable', this.agentsStateCollection.at(0));
    }

  }

  /**
   * Based on the availability response from VideoDesk
   * check various parameters to determine whether both
   * sides are able to initiate a video chat session
   *
   * Note: This method was provided by video desk and is undocumented
   *       and could change in the future
   *
   * Results of _vdk.env.result.bw_value test
   *
   * -10: is the default value. It means that the videochat
   *      module is not loaded (it's impossible to edit the
   *      local storage or the website is not configured for
   *      video chat sessions)
   *
   * -5: the function bwResult has never been called during
   *     the 15 seconds timer. It can happen if a page is very
   *     slow to load
   *
   * -4: the flash plugin is not installed or disabled on the browser
   *
   * -1: the UDP port 3478 is blocked.
   *
   * The test can be done again and the value updated at each
   * new page load if:
   *
   * - vdkEnv is not present in the localstorage
   * - vdkEnv is older than 15 minutes
   * - _vdk.env.result.bw_value is -4 or -10
   *
   * @param videoDeskAvailability
   * @returns {boolean}
   */
  checkUserCanChat(videoDeskAvailability) {
    let userCanChat = true;
    let videoDeskResult = window._vdk.env.result;
    let isAvailableForTextAudioOrVideo = (
      videoDeskAvailability.available_text ||
      videoDeskAvailability.available_audio ||
      videoDeskAvailability.available_video
    );
    if (!isAvailableForTextAudioOrVideo) {
      log.debug('VideoDesk: User cannot chat - no agents available for video, audio or text');
      userCanChat = false;
    } else if (videoDeskResult.bw_value <= 0) {
      let bwAbilityUnknownLog = 'VideoDesk: User chat ability unknown so allow - ';
      switch (videoDeskResult.bw_value) {
        case -10:
          log.debug(bwAbilityUnknownLog + 'videochat module has not loaded', videoDeskResult);
          break;
        case -5:
          log.debug(bwAbilityUnknownLog + 'videodesk not checked ability due to timeout', videoDeskResult);
          break;
        case -4:
          log.debug(bwAbilityUnknownLog + 'no flash plugin installed but might be able to use HTML5', videoDeskResult);
          break;
        case -1:
          log.debug(bwAbilityUnknownLog + 'UDP port 3478 is blocked but might have other ways of connecting', videoDeskResult);
          break;
        default:
          log.debug('VideoDesk: User cannot chat - bandwidth flag is 0 or less', videoDeskResult);
          userCanChat = false;
      }
    }
    return userCanChat;
  }

  /**
   * This shows a single agent with a call to action
   * currently saying "Need Help?"
   */
  createPreferentialBlockView() {
    let $prefBlock = $('.js-videoTextChatBlock');
    if ($prefBlock.length) {
      new VideoDeskBlock({
        el: $prefBlock,
        model: this.agentListCollection.at(0),
        isPreferentialBlock: true,
        globalSettings: this.globals
      });
    }
  }

  /**
   * This shows an agent block based on a specific uid of
   * that agent.
   */
  createSpecificAgentBlockViews() {
    $('.js-specificAgentChatBlock').each(_.bind(function(i, item) {
      let uid = $(item).data('uid'),
        templateName = $(item).data('templateName');
      if (!templateName) templateName = 'video';
      new VideoDeskBlock({
        el: $(item),
        model: this.agentsStateCollection.findWhere({ uid: uid }),
        templateName: templateName,
        globalSettings: this.globals
      });
    }, this));
  }

  /**
   * We build the agent collection from a global variable
   * written into the page from the server.
   */
  buildAgentList() {
    let $agentList = $('.js-fullAgentList');
    if (!$agentList.length) return;
    new AgentListView({
      el: $agentList,
      collection: this.agentListCollection,
      shouldShowContactUs: this.globals.showcontactus,
      globalSettings: this.globals
    });
  }
}



// WEBPACK FOOTER //
// ./assets/components/VideoDesk/VideoDeskUIController.js